/**
 * Pure drag-mode decision kernel for the sidebar's "exit drag mode when
 * clicking outside" behavior — the deterministic "should this click exit drag
 * mode?" predicate, testable without a full React render. The listener wiring
 * (add/remove on `mousedown`, gated by `isDragMode`) lives in
 * `useDragModeEffects`.
 */

/** Minimal DOM-element surface this kernel touches. */
export interface ClickTargetLike {
  closest(selector: string): unknown;
}

/** Minimal sidebar-container surface (the `sidebarRef.current`). */
export interface SidebarContainerLike {
  contains(node: unknown): boolean;
}

/**
 * Decide whether a `mousedown` should exit drag mode:
 *
 *   1. If the click is NOT inside the sidebar (container null, or
 *      `container.contains(target)` false) → exit. A null container reads as
 *      `undefined` (falsy), matching `sidebarRef.current?.contains(target)`.
 *   2. Otherwise, exit only if the target is NOT within a `.conversation-item`,
 *      `.drag-handle`, or `.delete-conversation-btn` (archive button).
 */
export function shouldExitDragModeOnClick(
  target: ClickTargetLike,
  container: SidebarContainerLike | null,
): boolean {
  const isInsideSidebar = container ? container.contains(target) : undefined;

  if (!isInsideSidebar) {
    return true;
  }

  const isConversationItem = target.closest('.conversation-item');
  const isDragHandle = target.closest('.drag-handle');
  const isArchiveButton = target.closest('.delete-conversation-btn');

  return !isConversationItem && !isDragHandle && !isArchiveButton;
}
